import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Pencil, Coins, TrendingUp, Layers } from 'lucide-react';
import { atlasApi, AtlasUser, Cargo, FaixaSalarial } from '../shared/atlasApi';
import FeedbackBanner, { Feedback } from '../shared/FeedbackBanner';
import { inputCls, labelCls } from '../shared/ListaEditavel';

interface Props {
  user: AtlasUser;
  podeEditar: boolean;
}

const brl = (v?: number | null) =>
  v === null || v === undefined
    ? '—'
    : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

// amplitude da faixa em % (max sobre min)
const amplitude = (f?: FaixaSalarial): number | null => {
  if (!f || f.min == null || f.max == null || f.min <= 0) return null;
  return Math.round(((f.max - f.min) / f.min) * 100);
};

const temFaixa = (f?: FaixaSalarial) =>
  !!f && (f.min != null || f.medio != null || f.max != null);

interface FormState {
  codigo: string;
  nome: string;
  departamento: string;
  nivel: number;
  min: string;
  medio: string;
  max: string;
}

const vazio = (): FormState => ({
  codigo: '', nome: '', departamento: '', nivel: 1, min: '', medio: '', max: '',
});

const numOrNull = (s: string): number | null => {
  const t = (s || '').trim().replace(/\./g, '').replace(',', '.');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const CargosSalarios: React.FC<Props> = ({ user, podeEditar }) => {
  const [itens, setItens] = useState<Cargo[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [modalAberto, setModalAberto] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(vazio());
  const [salvando, setSalvando] = useState(false);
  const [fb, setFb] = useState<Feedback>(null);

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    atlasApi.getCargos(user)
      .then(r => setItens(r.itens || []))
      .catch(e => setErro(e.message || 'Erro ao carregar cargos'))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { carregar(); }, [carregar]);

  const ordenados = useMemo(
    () => [...itens].sort((a, b) => (b.nivel || 0) - (a.nivel || 0) || a.nome.localeCompare(b.nome)),
    [itens]
  );

  const stats = useMemo(() => {
    const comFaixa = itens.filter(c => temFaixa(c.faixa_salarial));
    const medios = comFaixa
      .map(c => c.faixa_salarial?.medio)
      .filter((v): v is number => v != null);
    const mediaGeral = medios.length
      ? Math.round(medios.reduce((s, v) => s + v, 0) / medios.length)
      : null;
    return {
      total: itens.length,
      comFaixa: comFaixa.length,
      pctComFaixa: itens.length ? Math.round((comFaixa.length / itens.length) * 100) : 0,
      mediaGeral,
    };
  }, [itens]);

  const abrirNovo = () => {
    setEditId(null);
    setForm(vazio());
    setFb(null);
    setModalAberto(true);
  };

  const abrirEdicao = (c: Cargo) => {
    setEditId(c._id);
    setForm({
      codigo: c.codigo || '',
      nome: c.nome || '',
      departamento: c.departamento || '',
      nivel: c.nivel ?? 1,
      min: c.faixa_salarial?.min != null ? String(c.faixa_salarial.min) : '',
      medio: c.faixa_salarial?.medio != null ? String(c.faixa_salarial.medio) : '',
      max: c.faixa_salarial?.max != null ? String(c.faixa_salarial.max) : '',
    });
    setFb(null);
    setModalAberto(true);
  };

  const salvar = async () => {
    if (!editId) {
      if (!form.codigo.trim()) { setFb({ tipo: 'erro', msg: 'Código é obrigatório.' }); return; }
      if (!form.nome.trim()) { setFb({ tipo: 'erro', msg: 'Nome é obrigatório.' }); return; }
    }
    const faixa: FaixaSalarial = {
      min: numOrNull(form.min),
      medio: numOrNull(form.medio),
      max: numOrNull(form.max),
    };
    setSalvando(true);
    setFb(null);
    try {
      if (editId) {
        await atlasApi.editarCargo(user, editId, { faixa_salarial: faixa });
      } else {
        await atlasApi.criarCargo(user, {
          codigo: form.codigo.trim(),
          nome: form.nome.trim(),
          departamento: form.departamento.trim() || undefined,
          nivel: Number(form.nivel) || 1,
          faixa_salarial: faixa,
        } as Omit<Cargo, '_id' | 'empresaId'>);
      }
      setModalAberto(false);
      carregar();
    } catch (e: any) {
      setFb({ tipo: 'erro', msg: e.message || 'Erro ao salvar.' });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center"><Coins className="w-5 h-5" /></div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-gray-900">Cargos &amp; Salários</h2>
          <p className="text-sm text-gray-500">Faixas salariais por cargo e nível.</p>
        </div>
        {podeEditar && (
          <button onClick={abrirNovo} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center gap-2">
            <Plus className="w-4 h-4" /> Novo cargo
          </button>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-400 text-xs font-medium"><Layers className="w-4 h-4" /> Cargos</div>
          <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-400 text-xs font-medium"><Coins className="w-4 h-4" /> Com faixa definida</div>
          <p className="text-2xl font-bold text-gray-900 mt-1">{stats.comFaixa}<span className="text-sm font-medium text-gray-400"> · {stats.pctComFaixa}%</span></p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-400 text-xs font-medium"><TrendingUp className="w-4 h-4" /> Média (ponto médio)</div>
          <p className="text-2xl font-bold text-gray-900 mt-1">{brl(stats.mediaGeral)}</p>
        </div>
      </div>

      {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}

      {loading ? (
        <div className="p-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...</div>
      ) : ordenados.length === 0 ? (
        <div className="p-12 text-center text-gray-400 bg-white rounded-2xl border border-gray-200">Nenhum cargo cadastrado.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                <th className="px-4 py-3 font-medium">Cargo</th>
                <th className="px-4 py-3 font-medium text-center">Nível</th>
                <th className="px-4 py-3 font-medium text-right">Mínimo</th>
                <th className="px-4 py-3 font-medium text-right">Médio</th>
                <th className="px-4 py-3 font-medium text-right">Máximo</th>
                <th className="px-4 py-3 font-medium text-center">Amplitude</th>
                {podeEditar && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {ordenados.map(c => {
                const f = c.faixa_salarial;
                const amp = amplitude(f);
                return (
                  <tr key={c._id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">{c.nome}</div>
                      <div className="text-xs text-gray-400">{c.codigo}{c.departamento ? ` · ${c.departamento}` : ''}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-xs px-1.5 py-0.5 rounded bg-purple-50 text-purple-600">{c.nivel}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">{brl(f?.min)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{brl(f?.medio)}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{brl(f?.max)}</td>
                    <td className="px-4 py-3 text-center text-xs text-gray-500">{amp != null ? `${amp}%` : '—'}</td>
                    {podeEditar && (
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => abrirEdicao(c)} className="text-xs text-purple-600 hover:underline inline-flex items-center gap-1">
                          <Pencil className="w-3.5 h-3.5" /> Faixa
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modalAberto && (
        <div className="fixed inset-0 z-[10050] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setModalAberto(false)}>
          <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="font-bold text-gray-900 flex-1">{editId ? 'Editar faixa salarial' : 'Novo cargo'}</h3>
              <button onClick={() => setModalAberto(false)} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100">✕</button>
            </div>
            <div className="p-5 space-y-3">
              {editId ? (
                <p className="text-sm text-gray-500">
                  Defina a faixa salarial de <span className="font-semibold text-gray-700">{form.nome}</span>.
                  Para editar perfil DISC, responsabilidades e requisitos, use o módulo Perfil DISC.
                </p>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className={labelCls}>Código *</label>
                      <input className={inputCls} value={form.codigo} onChange={e => setForm(p => ({ ...p, codigo: e.target.value }))} placeholder="VEND01" />
                    </div>
                    <div className="md:col-span-2">
                      <label className={labelCls}>Nome *</label>
                      <input className={inputCls} value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} placeholder="Vendedor" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Departamento</label>
                      <input className={inputCls} value={form.departamento} onChange={e => setForm(p => ({ ...p, departamento: e.target.value }))} placeholder="Comercial" />
                    </div>
                    <div>
                      <label className={labelCls}>Nível</label>
                      <input className={inputCls} type="number" min={1} value={form.nivel} onChange={e => setForm(p => ({ ...p, nivel: Number(e.target.value) }))} />
                    </div>
                  </div>
                </>
              )}

              <div className="grid grid-cols-3 gap-3 pt-1">
                <div>
                  <label className={labelCls}>Mínimo (R$)</label>
                  <input className={inputCls} inputMode="numeric" value={form.min} onChange={e => setForm(p => ({ ...p, min: e.target.value }))} placeholder="2000" />
                </div>
                <div>
                  <label className={labelCls}>Médio (R$)</label>
                  <input className={inputCls} inputMode="numeric" value={form.medio} onChange={e => setForm(p => ({ ...p, medio: e.target.value }))} placeholder="2500" />
                </div>
                <div>
                  <label className={labelCls}>Máximo (R$)</label>
                  <input className={inputCls} inputMode="numeric" value={form.max} onChange={e => setForm(p => ({ ...p, max: e.target.value }))} placeholder="3000" />
                </div>
              </div>

              <FeedbackBanner feedback={fb} />

              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setModalAberto(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100">Cancelar</button>
                <button onClick={salvar} disabled={salvando} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center gap-2 disabled:opacity-60">
                  {salvando && <Loader2 className="w-4 h-4 animate-spin" />} Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CargosSalarios;
